import React, { useState, useEffect } from 'react';
import { useToast } from '../context/ToastContext';
import { FaCrown, FaUsers, FaKey, FaChartBar, FaTrash, FaToggleOn, FaToggleOff, FaCheck, FaTimes, FaSignOutAlt, FaEnvelope, FaSearch } from 'react-icons/fa';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import HeartLoader from '../components/HeartLoader';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const { logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userMessages, setUserMessages] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (activeTab === 'stats') {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/stats`, {
          headers
        });

        if (!response.ok) {
          throw new Error('Failed to fetch stats');
        }

        const data = await response.json();
        setStats(data);
      }
      
      if (activeTab === 'users') {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users`, {
          headers
        });

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const data = await response.json();
        setUsers(data);
      }
      
      if (activeTab === 'codes') {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/redeem-codes`, {
          headers
        });

        if (!response.ok) {
          throw new Error('Failed to fetch codes');
        }

        const data = await response.json();
        setCodes(data);
      }
    } catch (error) {
        showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/redeem-codes/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ count: 1, duration: 7 })
      });

      if (!response.ok) {
        throw new Error('Failed to generate code');
      }

      await fetchData();
      showToast('Code generated successfully!', 'success');
    } catch (error) {
      showToast('Failed to generate code', 'error');
    }
  };

  const updateUserStatus = async (userId, updates) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${userId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updates)
      });
      
      const data = await response.json();
      showToast('User status updated successfully', 'success');
      fetchData();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${userId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete user');
        }

        await fetchData();
        showToast('User deleted successfully!', 'success');
      } catch (error) {
        showToast('Failed to delete user', 'error');
      }
    }
  };

  const deleteCode = async (codeId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/redeem-codes/${codeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete code');
      }

      await fetchData();
      showToast('Code deleted successfully!', 'success');
    } catch (error) {
      showToast('Failed to delete code', 'error');
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/search?q=${query}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to search users');
      
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchUserMessages = async (userId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users/${userId}/messages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch messages');
      
      const data = await response.json();
      setUserMessages(data);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const renderUsersTable = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Username
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Email
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {users.map((user) => (
          <tr key={user._id} className={user.isAdmin ? 'bg-purple-50' : ''}>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex items-center">
                <div className="text-sm font-medium text-gray-900">
                  {user.username}
                </div>
                {user.isAdmin && (
                  <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                    Admin
                  </span>
                )}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">{user.email}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                user.isPremium ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {user.isPremium ? 'Premium' : 'Free'}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => updateUserStatus(user._id, { isPremium: !user.isPremium })}
                  className={`p-1.5 rounded-full transition-colors ${
                    user.isPremium ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                  } hover:bg-opacity-80`}
                  title={user.isPremium ? 'Disable Premium' : 'Enable Premium'}
                >
                  {user.isPremium ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                </button>
                {!user.isAdmin && (
                  <button
                    onClick={() => deleteUser(user._id)}
                    className="p-1.5 rounded-full text-red-600 bg-red-100 hover:bg-red-200 transition-colors"
                    title="Delete User"
                  >
                    <FaTrash size={14} />
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderCodesTable = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Code
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Used By
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Duration
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {codes.map((code) => (
          <tr key={code._id}>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm font-mono font-medium text-gray-900">{code.code}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                code.isUsed ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
                {code.isUsed ? <FaTimes className="mr-1" /> : <FaCheck className="mr-1" />}
                {code.isUsed ? 'Used' : 'Available'}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-500">
                {code.usedBy ? code.usedBy.username : '-'}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {code.duration} days
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {!code.isUsed && (
                <button
                  onClick={() => deleteCode(code._id)}
                  className="p-1.5 rounded-full text-red-600 bg-red-100 hover:bg-red-200 transition-colors"
                  title="Delete Code"
                >
                  <FaTrash size={14} />
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const StatsCharts = ({ stats }) => {
    const userStats = [
      { name: 'Regular Users', value: stats.totalUsers - stats.premiumUsers },
      { name: 'Premium Users', value: stats.premiumUsers }
    ];

    const codeStats = [
      { name: 'Available', value: stats.availableCodes },
      { name: 'Used', value: stats.totalCodes - stats.availableCodes }
    ];

    const GRADIENTS = [
      ['#c084fc', '#e879f9'],
      ['#d946ef', '#f0abfc']
    ];

    return (
      <div className="mt-4 sm:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* User Distribution Chart */}
        <div className="bg-white/70 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-white/20">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-4">User Distribution</h3>
          <div className="h-60 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <defs>
                  {GRADIENTS.map((colors, index) => (
                    <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors[0]} stopOpacity={1} />
                      <stop offset="100%" stopColor={colors[1]} stopOpacity={1} />
                    </linearGradient>
                  ))}
                </defs>
                <Pie
                  data={userStats}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {userStats.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={`url(#gradient-${index})`}
                      stroke="none"
                    />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '0.5rem',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="circle"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Redeem Codes Status */}
        <div className="bg-white/70 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-white/20">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-4">Redeem Codes Status</h3>
          <div className="h-60 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={codeStats}
                margin={{ top: 20, right: 10, left: 0, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorCodes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#c084fc" stopOpacity={0.8}/>
                    <stop offset="100%" stopColor="#e879f9" stopOpacity={0.3}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6b7280', fontSize: '12px' }}
                />
                <YAxis 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#6b7280', fontSize: '12px' }}
                  tickFormatter={(value) => value.toLocaleString()}
                  width={40}
                />
                <Tooltip
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '0.5rem',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar 
                  dataKey="value" 
                  fill="url(#colorCodes)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const GrowthChart = ({ data }) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get the current date
    const currentDate = new Date();
    
    // Create an array of the last 12 months
    const last12Months = Array.from({ length: 12 }, (_, i) => {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - (11 - i));
      return {
        month: date.getMonth() + 1,
        year: date.getFullYear()
      };
    });

    // Create a map of existing data for quick lookup
    const dataMap = new Map(
      data.map(item => [
        `${item._id.year}-${item._id.month}`,
        item.count
      ])
    );

    // Fill in the data, using 0 for missing months
    const formattedData = last12Months.map(({ month, year }) => {
      const key = `${year}-${month}`;
      return {
        name: `${monthNames[month - 1]} ${year}`,
        Users: dataMap.get(key) || 0
      };
    });

    return (
      <div className="col-span-full bg-white/70 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-white/20 mt-4 sm:mt-6">
        <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-4">Monthly User Growth</h3>
        <div className="h-60 sm:h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={formattedData}
              margin={{ top: 10, right: 10, left: -20, bottom: 40 }}
            >
              <defs>
                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#c084fc" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#e879f9" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#e5e7eb"
                vertical={false}
              />
              <XAxis 
                dataKey="name" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6b7280', fontSize: '10px' }}
                interval="preserveStartEnd"
                minTickGap={10}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6b7280', fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
                width={40}
              />
              <Tooltip
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '0.5rem',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value) => [value.toLocaleString(), 'Users']}
              />
              <Area 
                type="monotone" 
                dataKey="Users" 
                stroke="#c084fc"
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorUsers)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-indigo-100 flex flex-col">
      <div className="flex-1">
        <div className="bg-white/70 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Admin Dashboard
              </h1>
              <button
                onClick={logout}
                className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 text-sm font-medium text-purple-600 hover:text-pink-600 transition-colors"
              >
                <FaSignOutAlt className="mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white/70 backdrop-blur-md shadow-sm overflow-x-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-4 sm:space-x-8">
              <button
                onClick={() => setActiveTab('stats')}
                className={`${
                  activeTab === 'stats'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap inline-flex items-center px-1 py-3 sm:py-4 border-b-2 text-xs sm:text-sm font-medium transition-colors`}
              >
                <FaChartBar className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Statistics</span>
                <span className="sm:hidden">Stats</span>
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`${
                  activeTab === 'users'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap inline-flex items-center px-1 py-3 sm:py-4 border-b-2 text-xs sm:text-sm font-medium transition-colors`}
              >
                <FaUsers className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Users</span>
                <span className="sm:hidden">User</span>
              </button>
              <button
                onClick={() => setActiveTab('messages')}
                className={`${
                  activeTab === 'messages'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap inline-flex items-center px-1 py-3 sm:py-4 border-b-2 text-xs sm:text-sm font-medium transition-colors`}
              >
                <FaEnvelope className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Messages</span>
                <span className="sm:hidden">Msgs</span>
              </button>
              <button
                onClick={() => setActiveTab('codes')}
                className={`${
                  activeTab === 'codes'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap inline-flex items-center px-1 py-3 sm:py-4 border-b-2 text-xs sm:text-sm font-medium transition-colors`}
              >
                <FaKey className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">Redeem Codes</span>
                <span className="sm:hidden">Code</span>
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <HeartLoader size="large" />
            </div>
          ) : (
            <>
              {activeTab === 'stats' && stats && (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
                    {/* Stats cards */}
                    <StatsCard
                      title="Total Users"
                      value={stats.totalUsers}
                      icon={FaUsers}
                      color="blue"
                    />
                    <StatsCard
                      title="Premium Users"
                      value={stats.premiumUsers}
                      icon={FaCrown}
                      color="yellow"
                    />
                    <StatsCard
                      title="Total Codes"
                      value={stats.totalCodes}
                      icon={FaKey}
                      color="purple"
                    />
                    <StatsCard
                      title="Available Codes"
                      value={stats.availableCodes}
                      icon={FaKey}
                      color="green"
                    />
                  </div>
                  <StatsCharts stats={stats} />
                  <GrowthChart data={stats.userGrowth} />
                </>
              )}

              {activeTab === 'users' && (
                <div className="bg-white/70 backdrop-blur-sm shadow rounded-lg overflow-hidden">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0 mb-4">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900">
                        User Management
                      </h3>
                    </div>
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <div className="inline-block min-w-full align-middle">
                        {renderUsersTable()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'codes' && (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Redeem Codes
                      </h3>
                      <button
                        onClick={generateCode}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700"
                      >
                        Generate Code
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      {renderCodesTable()}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'messages' && (
                <div className="bg-white/70 backdrop-blur-sm shadow rounded-lg overflow-hidden">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="mb-6">
                      <label htmlFor="search" className="block text-lg font-medium text-gray-900 mb-4 sm:mb-6">
                        Search Users
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FaSearch className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="search"
                          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:border-purple-500 focus:ring-purple-500 text-base"
                          placeholder="Search by username or email..."
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            if (e.target.value.trim() === '') {
                              setSearchResults([]);
                              setSelectedUser(null);
                              setUserMessages([]);
                            } else {
                              searchUsers(e.target.value);
                            }
                          }}
                        />
                        {searchLoading && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-purple-600"></div>
                          </div>
                        )}
                      </div>
                      
                      {/* Search Results */}
                      {searchResults.length > 0 && (
                        <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                          {searchResults.map(user => (
                            <button
                              key={user._id}
                              onClick={() => {
                                setSelectedUser(user);
                                fetchUserMessages(user._id);
                              }}
                              className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                                selectedUser?._id === user._id
                                  ? 'bg-purple-100'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-gray-900">{user.username}</p>
                                  <p className="text-sm text-gray-500">{user.email}</p>
                                </div>
                                {user.isPremium && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    Premium
                                  </span>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Messages Display */}
                    {selectedUser && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-medium text-gray-900">
                              Messages for {selectedUser.username}
                            </h3>
                            <button
                              onClick={() => {
                                setSelectedUser(null);
                                setUserMessages([]);
                                setSearchQuery('');
                                setSearchResults([]);
                              }}
                              className="text-gray-400 hover:text-gray-600"
                              title="Clear selection"
                            >
                              <FaTimes className="h-4 w-4" />
                            </button>
                          </div>
                          <span className="text-sm text-gray-500">
                            Total: {userMessages.length}
                          </span>
                        </div>
                        
                        <div className="space-y-4 max-h-[600px] overflow-y-auto">
                          {userMessages.map(message => (
                            <div
                              key={message._id}
                              className="bg-white rounded-lg shadow-sm p-4 border border-gray-200"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div className="text-sm text-gray-500">
                                  {new Date(message.createdAt).toLocaleString()}
                                </div>
                                {message.isRead && (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Read
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-900 whitespace-pre-wrap">{message.content}</p>
                            </div>
                          ))}
                          
                          {userMessages.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                              No messages found for this user
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      <div className="mt-8 border-t border-black/10">
        <Footer />
      </div>
    </div>
  );
};

const StatsCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white/70 backdrop-blur-sm overflow-hidden rounded-xl sm:rounded-2xl shadow-lg border border-white/20">
    <div className="p-4 sm:p-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className={`p-2 sm:p-3 bg-${color}-100 rounded-lg sm:rounded-xl`}>
            <Icon className={`h-4 w-4 sm:h-6 sm:w-6 text-${color}-500`} />
          </div>
        </div>
        <div className="ml-3 sm:ml-5 w-0 flex-1">
          <dl>
            <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="text-lg sm:text-2xl font-semibold text-gray-900">{value.toLocaleString()}</dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
);

export default AdminDashboard; 